/**
 * Size Unit Converter
 *
 * Automatically converts product variant size labels from inches to centimeters
 * for customers outside imperial measurement regions (US, Liberia, Myanmar).
 *
 * Uses clean EU sizes (rounded to nearest whole cm) for better UI display:
 * - 13 x 10" → 33 × 25 cm
 * - 11 x 8.5" → 28 × 22 cm
 * - 8.5 x 11" → 22 × 28 cm
 * - 10 x 10" → 25 × 25 cm
 * - 8 x 8" → 20 × 20 cm
 * - 8 x 6" → 20 × 15 cm
 */

const IMPERIAL_COUNTRIES = ['US', 'LR', 'MM'];
const INCHES_TO_CM = 2.54;
const SIZE_CONVERTER_DEBUG = false; // Set to true for debugging
const SIZE_CONVERTER_FORCE_METRIC = false; // Set to true to force conversion for testing
let _sizeConverterTimeout;

/**
 * Clean EU size mappings - maps US inch sizes to rounded cm equivalents
 * Key format: "width,height" (normalized numbers)
 */
const CLEAN_SIZE_MAP = {
  '13,10': '33 × 25 cm',
  '11,8.5': '28 × 22 cm',
  '8.5,11': '22 × 28 cm',
  '10,10': '25 × 25 cm',
  '8,8': '20 × 20 cm',
  '8,6': '20 × 15 cm'
};

/**
 * Single dimension clean mappings
 */
const CLEAN_SINGLE_SIZE_MAP = {
  '13': '33 cm',
  '11': '28 cm',
  '10': '25 cm',
  '8.5': '22 cm',
  '8': '20 cm',
  '6': '15 cm'
};

/**
 * Check if size conversion should be applied based on current region
 * @param {string} countryCode - ISO country code
 * @returns {boolean}
 */
function shouldConvertToMetric(countryCode) {
  if (SIZE_CONVERTER_FORCE_METRIC) {
    console.log('[SizeConverter] FORCE_METRIC enabled - converting regardless of country');
    return true;
  }
  const shouldConvert = !IMPERIAL_COUNTRIES.includes(countryCode?.toUpperCase());
  if (SIZE_CONVERTER_DEBUG) {
    console.log('[SizeConverter] Country:', countryCode, '| Should convert to metric:', shouldConvert);
  }
  return shouldConvert;
}

/**
 * Convert inches to centimeters, rounding to nearest whole number
 * @param {number} inches
 * @returns {number}
 */
function inchesToCm(inches) {
  const cm = inches * INCHES_TO_CM;
  // Round to nearest whole number for clean display
  return Math.round(cm);
}

/**
 * Format a centimeter value for display
 * @param {number} cm
 * @returns {string}
 */
function formatCm(cm) {
  // Always show whole number
  return Math.round(cm).toString();
}

/**
 * Convert a size string from inches to centimeters
 *
 * Clean EU size mappings:
 * - "13" x 10"" -> "33 × 25 cm"
 * - "11 x 8.5"" -> "28 × 22 cm"
 * - "8.5" x 11"" -> "22 × 28 cm"
 * - "10" x 10"" -> "25 × 25 cm"
 * - "8 x 8"" -> "20 × 20 cm"
 * - "8 x 6"" -> "20 × 15 cm"
 * - Single dimension: "8"" -> "20 cm"
 *
 * @param {string} sizeStr - Original size string in inches
 * @returns {string} Converted size string in cm, or original if no match
 */
function convertSizeToMetric(sizeStr) {
  if (!sizeStr || typeof sizeStr !== 'string') return sizeStr;

  // Skip if already converted (contains 'cm') - prevents double conversion
  if (sizeStr.toLowerCase().includes('cm')) {
    if (SIZE_CONVERTER_DEBUG) {
      console.log('[SizeConverter] Skipping - already contains cm:', sizeStr);
    }
    return sizeStr;
  }

  // Skip if no inch marks found - means it's likely already in metric or not a size
  const hasInchMark = /["\u201c\u201d\u2033''`´]/.test(sizeStr);
  if (!hasInchMark) {
    if (SIZE_CONVERTER_DEBUG) {
      console.log('[SizeConverter] Skipping - no inch marks found:', sizeStr);
    }
    return sizeStr;
  }

  // Normalize different quote types to standard double quote
  // Handles: " (standard), \u201c \u201d (curly), \u2033 (prime/inch symbol), '' (single quotes used as inch marks)
  const normalized = sizeStr.trim()
    .replace(/["\u201c\u201d\u2033''`´]/g, '"');

  if (SIZE_CONVERTER_DEBUG && normalized !== sizeStr.trim()) {
    console.log('[SizeConverter] Normalized quotes:', JSON.stringify(sizeStr), '->', JSON.stringify(normalized));
  }

  // Pattern for dimension pairs: "13" x 10"" or "13 x 10""
  // Both numbers may have inch marks, or just the last one
  const dimensionPattern = /^(\d+(?:\.\d+)?)["']?\s*[x×]\s*(\d+(?:\.\d+)?)["']?$/i;

  // Pattern for single dimension: "8""
  const singlePattern = /^(\d+(?:\.\d+)?)["']?$/;

  let match = normalized.match(dimensionPattern);
  if (match) {
    const width = parseFloat(match[1]);
    const height = parseFloat(match[2]);

    // Check clean size map first
    const sizeKey = `${width},${height}`;
    if (CLEAN_SIZE_MAP[sizeKey]) {
      if (SIZE_CONVERTER_DEBUG) {
        console.log('[SizeConverter] Using clean size mapping:', sizeKey, '->', CLEAN_SIZE_MAP[sizeKey]);
      }
      return CLEAN_SIZE_MAP[sizeKey];
    }

    // Fallback to calculated conversion (rounded to whole numbers)
    const widthCm = formatCm(inchesToCm(width));
    const heightCm = formatCm(inchesToCm(height));
    return `${widthCm} × ${heightCm} cm`;
  }

  match = normalized.match(singlePattern);
  if (match) {
    const dimension = parseFloat(match[1]);

    // Check clean single size map first
    const dimKey = dimension.toString();
    if (CLEAN_SINGLE_SIZE_MAP[dimKey]) {
      return CLEAN_SINGLE_SIZE_MAP[dimKey];
    }

    // Fallback to calculated conversion
    const dimensionCm = formatCm(inchesToCm(dimension));
    return `${dimensionCm} cm`;
  }

  // No conversion needed/possible
  if (SIZE_CONVERTER_DEBUG && (sizeStr.includes('"') || sizeStr.includes('x'))) {
    console.log('[SizeConverter] No match for:', JSON.stringify(sizeStr), 'normalized:', JSON.stringify(normalized));
  }
  return sizeStr;
}

/**
 * Convert text content of an element if it contains size pattern
 * @param {Element} element
 * @param {boolean} preserveOriginal - Store original value in data attribute
 */
function convertElementText(element, preserveOriginal = true) {
  const originalText = element.textContent.trim();
  const convertedText = convertSizeToMetric(originalText);

  if (convertedText !== originalText) {
    if (preserveOriginal && !element.dataset.originalValue) {
      element.dataset.originalValue = originalText;
    }
    element.textContent = convertedText;
    return true;
  }
  return false;
}

/**
 * Convert text within an element that contains a product name and size
 * Pattern: "Product Name - 13" x 10"" -> "Product Name - 33 × 25 cm"
 * @param {Element} element
 */
function convertProductTitleWithSize(element) {
  if (element.dataset.converted) return false;

  const text = element.textContent.trim();
  
  // Skip if already contains 'cm' - already converted
  if (text.toLowerCase().includes('cm')) {
    if (SIZE_CONVERTER_DEBUG) {
      console.log('[SizeConverter] Skipping card - already contains cm:', text);
    }
    return false;
  }
  
  // Skip if no inch marks - not in inches format
  if (!/["\u201c\u201d\u2033''`´]/.test(text)) {
    if (SIZE_CONVERTER_DEBUG) {
      console.log('[SizeConverter] Skipping card - no inch marks:', text);
    }
    return false;
  }
  
  // Normalize quotes - handle all quote types
  const normalized = text.replace(/["\u201c\u201d\u2033''`´]/g, '"');

  // Match patterns like "Product Name - 13" x 10"" or "Product Name - 13 x 10""
  // More flexible pattern to handle various spacing and quote positions
  const pattern = /^(.+?)\s*[-–—]\s*(\d+(?:\.\d+)?)["']?\s*[x×]\s*(\d+(?:\.\d+)?)["']?\s*$/i;
  const match = normalized.match(pattern);

  if (SIZE_CONVERTER_DEBUG) {
    console.log('[SizeConverter] Card heading original:', JSON.stringify(text));
    console.log('[SizeConverter] Card heading normalized:', JSON.stringify(normalized));
    console.log('[SizeConverter] Pattern match result:', match);
  }

  if (match) {
    const productName = match[1].trim();
    const width = parseFloat(match[2]);
    const height = parseFloat(match[3]);
    const widthCm = formatCm(inchesToCm(width));
    const heightCm = formatCm(inchesToCm(height));

    element.dataset.originalValue = text;
    element.dataset.converted = 'true';
    element.textContent = `${productName} - ${widthCm} × ${heightCm} cm`;

    if (SIZE_CONVERTER_DEBUG) {
      console.log('[SizeConverter] ✓ Converted card heading to:', element.textContent);
    }
    return true;
  }
  return false;
}

/**
 * Apply metric conversion to variant option elements in the DOM
 * @param {string} countryCode - Current customer's country code
 * @param {Document|Element} [root=document] - Scope for DOM queries; pass an added node to limit re-scan cost
 */
function applyMetricConversion(countryCode, root = document) {
  if (!shouldConvertToMetric(countryCode)) return;

  // Convert button/radio labels in variant pickers - directly convert the label text
  const labels = root.querySelectorAll('.product-form__input--pill label, .product-form__input--swatch label');
  if (SIZE_CONVERTER_DEBUG) {
    console.log('[SizeConverter] Found', labels.length, 'variant labels');
  }

  labels.forEach(label => {
    // Skip if already converted
    if (label.dataset.converted) return;

    // Get the visible text content (first text node or full text)
    const walker = document.createTreeWalker(label, NodeFilter.SHOW_TEXT, null, false);
    let textNode;
    while (textNode = walker.nextNode()) {
      const originalText = textNode.textContent.trim();
      if (originalText) {
        if (SIZE_CONVERTER_DEBUG) {
          console.log('[SizeConverter] Processing label text:', JSON.stringify(originalText));
        }
        const convertedText = convertSizeToMetric(originalText);
        if (convertedText !== originalText) {
          if (SIZE_CONVERTER_DEBUG) {
            console.log('[SizeConverter] Converting:', originalText, '->', convertedText);
          }
          textNode.textContent = textNode.textContent.replace(originalText, convertedText);
          label.dataset.converted = 'true';
          label.dataset.originalValue = originalText;
        }
      }
    }
  });

  // Convert dropdown options
  root.querySelectorAll('.product-form__input--dropdown select option').forEach(option => {
    const originalText = option.textContent;
    const convertedValue = convertSizeToMetric(originalText);
    if (convertedValue !== originalText) {
      option.dataset.originalValue = option.value;
      option.textContent = convertedValue;
    }
  });

  // Convert selected value displays (spans showing currently selected option)
  root.querySelectorAll('[data-selected-value]').forEach(span => {
    convertElementText(span);
  });

  // Convert cart drawer and cart page option values
  root.querySelectorAll('.product-option dd').forEach(dd => {
    convertElementText(dd);
  });

  // Convert card-variant titles that contain "Product - Size" pattern
  // Selects links within card headings that display variant info (collection pages)
  const cardHeadings = root.querySelectorAll('.card__heading a');
  if (SIZE_CONVERTER_DEBUG) {
    console.log('[SizeConverter] Found', cardHeadings.length, 'card heading links to check');
    cardHeadings.forEach((el, i) => {
      console.log(`[SizeConverter] Card ${i}:`, JSON.stringify(el.textContent.trim()));
    });
  }
  cardHeadings.forEach(el => {
    if (el.dataset.converted) return;

    const text = el.textContent.trim();
    
    // Skip if already contains 'cm' - already converted by Liquid
    if (text.toLowerCase().includes('cm')) return;
    
    // Check if it has the "Product - Size" pattern with inch marks (NOT just x separator)
    // Must have inch marks to be considered for conversion
    if (text.includes(' - ') && /["\u201c\u201d\u2033''`´]/.test(text)) {
      if (SIZE_CONVERTER_DEBUG) {
        console.log('[SizeConverter] Attempting to convert card:', JSON.stringify(text));
      }
      convertProductTitleWithSize(el);
    }
  });

  // Convert order page variant titles
  root.querySelectorAll('.order-item-details .product-option dd').forEach(dd => {
    convertElementText(dd);
  });

  // Convert facet filter labels (size filters in collection pages)
  root.querySelectorAll('.facet-checkbox__text-label').forEach(span => {
    convertElementText(span);
  });

  // Convert active filter tags
  root.querySelectorAll('.active-facets__button-inner').forEach(span => {
    const originalText = span.textContent;
    // Pattern: "Filter Name: Value" - only convert the value part
    const match = originalText.match(/^(.+?):\s*(.+)$/);
    if (match) {
      const filterName = match[1];
      const filterValue = match[2].trim();
      const convertedValue = convertSizeToMetric(filterValue);
      if (convertedValue !== filterValue) {
        span.dataset.originalValue = originalText;
        span.textContent = `${filterName}: ${convertedValue}`;
      }
    }
  });

  // Convert any elements marked with data-convert-size attribute
  root.querySelectorAll('[data-convert-size]').forEach(el => {
    convertElementText(el);
  });
}

/**
 * Update the selected value display when variant changes
 * @param {Event} event - Change event from variant picker
 */
function handleVariantChange(event) {
  const countryCode = window.Shopify?.country || document.documentElement.dataset.countryCode;
  if (!shouldConvertToMetric(countryCode)) return;

  // Find and update selected value displays after a short delay to allow DOM update
  setTimeout(() => {
    document.querySelectorAll('[data-selected-value]').forEach(span => {
      const originalText = span.textContent.trim();
      const convertedValue = convertSizeToMetric(originalText);
      if (convertedValue !== originalText) {
        span.textContent = convertedValue;
      }
    });
  }, 10);
}

/**
 * Initialize size unit converter
 */
function initSizeUnitConverter() {
  const countryCode = window.Shopify?.country || document.documentElement.dataset.countryCode;

  applyMetricConversion(countryCode);

  // Listen for variant changes to update displays
  document.addEventListener('change', (e) => {
    if (e.target.closest('variant-selects')) {
      handleVariantChange(e);
    }
  });

  // Re-apply after dynamic content loads (e.g., quick add modals, cart updates, facet filters)
  // Skip entirely for non-metric users — no conversion needed
  if (shouldConvertToMetric(countryCode)) {
    const observer = new MutationObserver((mutations) => {
      const nodesToConvert = [];

      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Only queue nodes that contain convertible content
            if (node.matches?.('variant-selects, cart-drawer, quick-add-modal, .cart-item, .product-option, .facets__item, .active-facets') ||
                node.querySelector?.('variant-selects, .cart-item, .product-option, .card__heading, quick-add-modal, .facet-checkbox__text-label')) {
              nodesToConvert.push(node);
            }
          }
        });
      });

      if (nodesToConvert.length) {
        // Debounce the reapplication; convert only within the added nodes
        clearTimeout(_sizeConverterTimeout);
        _sizeConverterTimeout = setTimeout(() => {
          nodesToConvert.forEach(node => applyMetricConversion(countryCode, node));
        }, 50);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }
}

// Auto-initialize when DOM is ready
if (SIZE_CONVERTER_DEBUG) {
  console.log('[SizeConverter] Script loaded. Document state:', document.readyState);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSizeUnitConverter);
} else {
  initSizeUnitConverter();
}

// Export for module use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { convertSizeToMetric, shouldConvertToMetric, inchesToCm };
}
